import { ActionIcon, Button, Loader, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDeviceFloppy,
  IconExclamationCircle,
  IconFilter,
  IconMinus,
  IconPlus,
  IconStrikethrough,
} from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { DataTable } from '~/components/tables/common/DataTable';
import DataTableFilterControl from '~/components/tables/common/DataTableFilterControl';
import DataTablePaginationControls from '~/components/tables/common/DataTablePaginationControls';
import { DataTableResultCount } from '~/components/tables/common/DataTableResultCount';
import DataTableRowsSelected from '~/components/tables/common/DataTableRowsSelected';
import DataTableSearchBox from '~/components/tables/common/DataTableSearchBox';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useSuiteAddTests } from '~/hooks/testSuite/useSuiteAddTests';
import { queryParamToString } from '~/logic/query-param';
import type { TestListItem } from '~/server/api/routers/test';
import type { TestSuiteFetchResult } from '~/server/api/routers/test-suite-router';
import { api } from '~/trpc-extra/api';
import { useDataTable } from '../tables/common/useDataTable';
import { testSuiteTestsColumns } from '../tables/testSuite/testSuiteTestsColumns';
import { TestDrawer } from '../test/TestDrawer';

// Wrap the component with the TestDrawerProvider to make the context available
export default function TestSuiteTestsContainer() {
  return (
    <>
      <TestSuiteTestsView />
      <TestDrawer />
    </>
  );
}

export function TestSuiteTestsView() {
  const router = useRouter();
  const projectId = useProjectId();
  const testSuiteId = queryParamToString(router.query.testSuiteId);

  const { data: tests, isLoading: testsLoading } = api.test.listTests.useQuery({ projectId: projectId! });
  const { data: testSuite, isLoading: testSuiteLoading } = api.testSuite.get.useQuery({
    projectId: projectId!,
    testSuiteId,
  });

  const isLoading = testSuiteLoading || testsLoading;

  return (
    isLoading ?
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    : testSuite ?
      <TestSuiteTestsTable
        tests={tests ?? []}
        testSuite={testSuite}
      />
    : null // this should never show
  );
}

function TestSuiteTestsTable({ tests, testSuite }: { tests: TestListItem[]; testSuite: TestSuiteFetchResult }) {
  const projectId = useProjectId();
  const testSuiteId = testSuite.id;
  const trpcUtils = api.useUtils();
  const [showFilter, setShowFilter] = useState(false);

  const { mutateAsync: updateTestMembership } = api.testSuite.updateTests.useMutation();

  const { addTestsToSuiteHandler, suiteMemberTests } = useSuiteAddTests({
    tests,
    testSuite,
  });

  const { data: userFlows = [] } = api.userFlow.list.useQuery({ projectId: projectId! }, { enabled: !!projectId });
  const { data: tags = [] } = api.tag.list.useQuery({ projectId: projectId! }, { enabled: !!projectId });

  const handleRemoveTestsFromSuite = async (testIds: string[]) => {
    table.setRowSelection({});
    try {
      await updateTestMembership({
        operation: 'remove',
        projectId: projectId!,
        testSuiteId,
        testIds,
      });
      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
      notifications.show({
        color: 'teal',
        message: `${testIds.length} test${testIds.length === 1 ? '' : 's'} removed from suite`,
        icon: <IconMinus />,
      });
      table.setRowSelection({});
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    }
  };

  const handleSkipTests = async (testIds: string[], skip: boolean) => {
    try {
      table.setRowSelection({});
      await updateTestMembership({
        operation: skip ? 'skip' : 'unskip',
        projectId: projectId!,
        testSuiteId,
        testIds,
      });
      await trpcUtils.testSuite.get.invalidate();
      notifications.show({
        color: 'teal',
        message: `Test ${skip ? 'skipped' : 'unskipped'}`,
        icon: <IconDeviceFloppy />,
      });
    } catch (e) {
      console.debug(e);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    }
  };

  const table = useDataTable({
    columns: testSuiteTestsColumns({
      userFlows,
      tags,
      tests: suiteMemberTests,
      handleSkipTests,
      handleRemoveTestsFromSuite,
    }),
    data: suiteMemberTests,
    enableQueryParam: true,
    enablePagination: true,
  });

  return (
    <div className="flex h-full flex-col gap-4 pt-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex w-full flex-wrap items-center gap-2">
            <div className="flex w-full flex-col items-start gap-2 md:w-auto md:flex-row md:items-center">
              {table.getFilteredSelectedRowModel().rows.length > 0 ?
                <div className="flex w-full items-center gap-2">
                  <Button
                    className="w-full md:w-auto"
                    color="blue"
                    size="compact-sm"
                    title="Skip Tests"
                    leftSection={<IconStrikethrough size={16} />}
                    onClick={() =>
                      handleSkipTests(
                        table.getFilteredSelectedRowModel().rows.map((row) => row.original.id),
                        // Toggle based on current state - skip if not all are skipped, unskip if all are skipped
                        !table.getFilteredSelectedRowModel().rows.every((row) => row.original.skip),
                      )
                    }
                  >
                    {table.getFilteredSelectedRowModel().rows.every((row) => row.original.skip) ?
                      'Unskip Tests'
                    : 'Skip Tests'}
                  </Button>
                  <Button
                    className="w-full md:w-auto"
                    color="red"
                    size="compact-sm"
                    title="Remove From Suite"
                    leftSection={<IconMinus size={16} />}
                    onClick={() =>
                      handleRemoveTestsFromSuite(table.getFilteredSelectedRowModel().rows.map((row) => row.original.id))
                    }
                  >
                    Remove From Suite
                  </Button>
                </div>
              : <Button
                  className="w-full md:w-auto md:flex-shrink-0"
                  color="blue"
                  title="Add Tests"
                  size="compact-sm"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => addTestsToSuiteHandler()}
                >
                  Add Tests
                </Button>
              }
              <div className="flex w-full items-center gap-2">
                <div className="flex-1">
                  <DataTableSearchBox table={table} />
                </div>
                <Tooltip label="Filter">
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color={showFilter ? 'blue' : 'gray'}
                    onClick={() => setShowFilter((prev) => !prev)}
                  >
                    <IconFilter size={16} />
                  </ActionIcon>
                </Tooltip>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <DataTableResultCount
                table={table}
                suffix={`test${table.getFilteredRowModel().rows.length === 1 ? '' : 's'}`}
                className="whitespace-nowrap"
              />
              {table.getFilteredSelectedRowModel().rows.length > 0 && (
                <DataTableRowsSelected
                  table={table}
                  className="whitespace-nowrap"
                />
              )}
            </div>
          </div>
        </div>
        {!!showFilter && <DataTableFilterControl table={table} />}
      </div>

      <DataTable
        table={table}
        emptyMessage={
          <div className="flex flex-col items-start gap-6 py-12">
            <div className="flex flex-col items-start gap-1">
              <Title order={4}>You don&apos;t have any tests in this test suite.</Title>
              <Text>Add tests to the suite to get started!</Text>
            </div>
          </div>
        }
      />
      <DataTablePaginationControls
        itemName="test"
        table={table}
        showSelectedCount={false}
      />
    </div>
  );
}
